type TDate = number | Date | string | undefined | null;
type DateTypes = Date | string | null | undefined;

const isDateOnlyString = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s.trim());
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

const toDateSafe = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return null;
    if (DATE_ONLY_RE.test(s)) {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d); // local midnight
    }
    const parsed = new Date(s);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof v === 'number') {
    const ms = v < 1_000_000_000_000 ? v * 1000 : v;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

export const formatStringDateToUTC = (input: Date | string | null | undefined) => {
  if (!input) return '';
  const d = toDateSafe(input);
  if (!d) return '';
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

export const formatStringDateToYYYYmmDD = (input: Date | string | null | undefined) => {
  if (!input) return '';

  // If already YYYY-MM-DD, don’t reparse (prevents drift)
  if (typeof input === 'string') {
    const s = input.trim();
    if (!s) return '';
    if (DATE_ONLY_RE.test(s)) return s;

    const d = toDateSafe(s);
    if (!d) return '';
    return toYYYYMMDDLocal(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
  }

  // Date instance
  const d = input as Date;
  return toYYYYMMDDLocal(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
};

const toYYYYMMDDLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const coerceToLocalDateOnly = (v: unknown): Date | null => {
  if (!v) return null;

  if (v instanceof Date) {
    if (isNaN(v.getTime())) return null;
    return new Date(v.getFullYear(), v.getMonth(), v.getDate());
  }

  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return null;

    if (DATE_ONLY_RE.test(s)) {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d);
    }

    const parsed = new Date(s);
    if (isNaN(parsed.getTime())) return null;
    return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
  }

  return null;
};

export const formatDateOnly = (input: string | Date | undefined | null): string => {
  const d = toDateSafe(input ?? null);
  if (!d) return '';
  return _formatDateOnly(d);
};

const _formatDateOnly = (date: Date) => {
  return new Intl.DateTimeFormat(navigator.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

export const toDate = (input: number | Date | string | undefined | null) => {
  if (typeof input === 'string') {
    const date = new Date(input);
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof input === 'number') {
    const date = new Date(input);
    return isNaN(date.getTime()) ? null : date;
  }

  return input;
};

export const formatDateToMMDD = (input: TDate) => {
  if (!input) return '';
  const dateObject = toDateSafe(input);
  if (!dateObject) return '';

  return new Intl.DateTimeFormat(navigator.language, {
    month: '2-digit',
    day: '2-digit',
  }).format(dateObject);
};

export const formatDateToMMDDYYYY = (input: TDate) => {
  if (!input) return '';
  const dateObject = toDateSafe(input);
  if (!dateObject) return '';
  return new Intl.DateTimeFormat(navigator.language, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(dateObject);
};

export const formatUSLocalTime = (apiTimeString: string) => {
  const fixedString = apiTimeString.replace('Z', '');

  const date = new Date(fixedString);

  return date.toLocaleTimeString('en-US', {
    timeZone: 'America/Chicago',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const formatTime = (date: TDate) => {
  if (!date) return '';
  const dateObject = toDateSafe(date);
  if (!dateObject) return '';

  return new Intl.DateTimeFormat(navigator.language, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObject);
};

export const formatUTCTime = (date: TDate) => {
  if (!date) return '';
  const dateObject = toDateSafe(date);
  if (!dateObject) return '';

  return new Intl.DateTimeFormat(navigator.language, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(dateObject);
};

export const formatDate = (date: TDate) => {
  if (!date) return '';

  if (typeof date === 'string' && isDateOnlyString(date)) {
    return formatDateOnly(date);
  }

  const dateObject = toDateSafe(date);

  if (!dateObject) return '';

  return new Intl.DateTimeFormat(navigator.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(dateObject);
};

export const getDateWithoutClientTimeZone = (date: TDate) => {
  if (!date) return '';

  return formatDate(`${date}T12:00:00Z`);
};

export const formatUTCDate = (date: TDate) => {
  if (!date) return '';
  const dateObject = toDateSafe(date);
  if (!dateObject) return '';

  return new Intl.DateTimeFormat(navigator.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(dateObject);
};

export const formatTimeSpan = (date: Date | null | undefined) => {
  if (!date) return '';

  return date.toLocaleTimeString(navigator.language, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatTimeSpanToHHMM = (date: Date | null | undefined) => {
  if (!date) return '';

  return date.toTimeString();
};

export const formatForHidingDate = (date: TDate) => {
  if (!date) return '';
  const dateObject = toDateSafe(date);
  if (!dateObject) return '';

  return new Intl.DateTimeFormat(navigator.language, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).format(dateObject);
};

export const formatForHidingExpDate = (date: TDate) => {
  if (!date) return '';
  const dateObject = toDateSafe(date);
  if (!dateObject) return '';

  return new Intl.DateTimeFormat(navigator.language, {
    month: 'numeric',
    year: 'numeric',
  }).format(dateObject);
};

export const formatDateTime = (date: TDate) => {
  if (!date) return '';
  const dateObject = toDateSafe(date);
  if (!dateObject) return '';

  return new Intl.DateTimeFormat(navigator.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObject);
};

export const formatDateFromToday = (date: TDate) => {
  if (!date) return '';
  const dateObject = new Date(date);
  const now = new Date();
  const elapsed = dateObject.getTime() - now.getTime();
  const minutes = Math.floor(elapsed / (1000 * 60));
  const hours = Math.floor(elapsed / (1000 * 60 * 60));
  const days = Math.floor(elapsed / (1000 * 60 * 60 * 24));

  if (elapsed < 0) {
    return formatPastDate(minutes * -1, hours * -1, days * -1);
  } else return formatFutureDate(minutes, hours, days);
};

export const formatRelativeTime = (date: Date | string | undefined): string => {
  if (!date) return '';
  const dateObject = new Date(date);
  const now = new Date();
  const elapsed = now.getTime() - dateObject.getTime();

  const minutes = Math.floor(elapsed / (1000 * 60));
  const hours = Math.floor(elapsed / (1000 * 60 * 60));
  const days = Math.floor(elapsed / (1000 * 60 * 60 * 24));
  return formatPaste(minutes, hours, days);
};

export const getClosestQuarterHour = (date: Date) => {
  const currentMinutes = date.getMinutes();
  const remainder = currentMinutes % 15;
  const minutesToAdd = remainder === 0 ? 15 : 15 - remainder;

  const nextQuarterHour = new Date(date.getTime() + minutesToAdd * 60000);
  nextQuarterHour.setSeconds(0);
  nextQuarterHour.setMilliseconds(0);

  return nextQuarterHour;
};

export const formatDateToMidnightUTC = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T00:00:00.000000Z`;
};

const formatPaste = (minutes: number, hours: number, days: number) => {
  if (minutes < 1) {
    return 'Just now';
  }
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const formatFuture = (minutes: number, hours: number, days: number) => {
  if (minutes < 5) {
    return 'Now';
  }
  if (minutes < 60) {
    return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  if (hours < 24) {
    return `in ${hours} hour${hours === 1 ? '' : 's'} `;
  }
  return `in ${days} day${days === 1 ? '' : 's'}`;
};

const formatFutureDate = (minutes: number, hours: number, days: number) => {
  return formatFuture(minutes, hours, days);
};

const formatPastDate = (minutes: number, hours: number, days: number) => {
  return formatPaste(minutes, hours, days);
};

export const formatDateToDayOfWeek = (date: TDate) => {
  if (!date) return '';

  const dateObject = toDate(date);

  if (!dateObject) return '';

  return new Intl.DateTimeFormat(navigator.language, {
    weekday: 'long',
  }).format(dateObject);
};

export const formatDateToMonthYear = (date: TDate) => {
  if (!date) return '';

  const dateObject = toDateSafe(date);
  if (!dateObject) return '';

  return new Intl.DateTimeFormat(navigator.language, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(dateObject);
};

export const extractMonthYearForFilter = (date: TDate) => {
  if (!date) return '';

  const dateObject = toDateSafe(date);
  if (!dateObject) return '';

  const year = dateObject.getUTCFullYear();
  const month = String(dateObject.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

export const differenceInDaysFromToday = (date: DateTypes) => {
  const inputDate = toDateSafe(date ?? null);

  if (!inputDate) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  inputDate.setHours(0, 0, 0, 0);

  const diffInMs = inputDate.getTime() - today.getTime();
  return Math.round(diffInMs / (1000 * 60 * 60 * 24));
};

export const formatTimeFromToday = (date: DateTypes) => {
  const diff = differenceInDaysFromToday(date);

  if (diff === null) {
    return null;
  }
  if (diff < 0) {
    return `${Math.abs(diff)} days ago`;
  }
  if (diff > 0) {
    return `${diff} days`;
  }
  return 'Today';
};

export const isSameMonthYear = (date1: TDate, date2: TDate) => {
  if (!date1 || !date2) return false;

  const dateObj1 = toDateSafe(date1);
  const dateObj2 = toDateSafe(date2);

  if (!dateObj1 || !dateObj2) return false;

  return (
    dateObj1.getUTCFullYear() === dateObj2.getUTCFullYear() &&
    dateObj1.getUTCMonth() === dateObj2.getUTCMonth()
  );
};

export const createMidnightDates = (inputDate: Date | string | undefined) => {
  if (!inputDate) throw new Error('Input date must be provided');

  let currentDate;
  if (typeof inputDate === 'string') {
    currentDate = toDateSafe(inputDate) ?? new Date();
  } else {
    currentDate = inputDate;
  }
  const midnightLocal = new Date(currentDate);

  midnightLocal.setHours(0, 0, 0, 0);

  const nextDayLocal = new Date(currentDate);
  nextDayLocal.setDate(nextDayLocal.getDate() + 1);

  nextDayLocal.setHours(0, 0, 0, 0);

  const midnightOfCurrentUtc = new Date(
    midnightLocal.getTime() + midnightLocal.getTimezoneOffset() * 60000,
  );
  const midnightNextDayUtc = new Date(
    nextDayLocal.getTime() + nextDayLocal.getTimezoneOffset() * 60000,
  );

  return {
    midnightOfCurrentUtc,
    midnightNextDayUtc,
  };
};

export const setColorClassBasedOnTimeDifference = (
  date: DateTypes,
  greenThreshold = 10,
  yellowThreshold = 5,
) => {
  const diff = differenceInDaysFromToday(date);
  if (diff === null) {
    return 'text-gray-500';
  }
  if (diff > greenThreshold) {
    return 'text-green-500';
  }
  if (diff <= greenThreshold && diff > yellowThreshold) {
    return 'text-yellow-700';
  }
  if (diff <= yellowThreshold) {
    return 'text-red-500';
  }

  return 'text-gray-500';
};
